import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useUser } from "@/context/UserContext";
import { useI18n } from "@/i18n/I18nContext";
import { listRoutes, listMessages } from "@/lib/api";
import { setMyRoutes } from "@/lib/myRoutes";

/**
 * Mounts global REST polling to every active route the user belongs
 * to (driver or joined passenger). When a chat message arrives from another
 * user the user is shown a toast popup ("Nuevo mensaje de X en tu ruta").
 *
 * Modified for Vercel Serverless environment (which drops WebSockets).
 */
const POLL_MS = 15000;

const RouteChatNotifier = () => {
  const { user } = useUser();
  const { t } = useI18n();
  const navigate = useNavigate();
  const seenCountsRef = useRef(new Map());
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const seenCounts = seenCountsRef.current;

    const sync = async () => {
      let routes;
      try {
        routes = await listRoutes();
      } catch {
        return;
      }
      if (cancelled) return;
      setMyRoutes(routes);
      const me = userRef.current?.id;
      const active = (routes || []).filter((r) => {
        if (r.status === "completed" || r.status === "cancelled") return false;
        const isDriver = r?.driver?.id === me;
        const isPassenger = (r?.passengers || []).some((p) => p.id === me);
        return isDriver || isPassenger;
      });
      const activeIds = new Set(active.map((r) => r.id));

      for (const rid of seenCountsRef.current.keys()) {
        if (!activeIds.has(rid)) {
          seenCountsRef.current.delete(rid);
        }
      }

      for (const r of active) {
        try {
          const data = await listMessages(r.id);
          if (cancelled) return;
          const msgs = data.messages || [];
          const currentCount = msgs.length;
          const previousCount = seenCountsRef.current.get(r.id);

          if (previousCount !== undefined && currentCount > previousCount) {
            const newMsgs = msgs.slice(previousCount);
            for (const m of newMsgs) {
              const meId = userRef.current?.id;
              if (!meId || m.user_id === meId) continue;
              const preview = (m.text || "").slice(0, 90);
              toast(
                t("chat.notify_title", { name: m.user_name || t("chat.notify_someone") }),
                {
                  description: preview,
                  duration: 6000,
                  action: {
                    label: t("chat.notify_open"),
                    onClick: () => {
                      window.location.hash = "#route-" + r.id;
                      if (window.location.pathname !== "/") {
                        navigate("/#route-" + r.id);
                      }
                    },
                  },
                }
              );
            }
          }
          seenCountsRef.current.set(r.id, currentCount);
        } catch {
          // ignore
        }
      }
    };

    sync();
    const iv = setInterval(sync, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(iv);
      seenCounts.clear();
    };
  }, [user?.id, t, navigate]);

  return null;
};

export default RouteChatNotifier;
